import { useEffect, useState } from "react";
import * as React from "react";

import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Divider,
  DialogActions,
  Switch,
  Box,
  Typography,
  useTheme,
  Autocomplete,
} from "@mui/material";

import * as Yup from "yup";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik, FormikProvider, Form } from "formik";
import {
  ResumeAdminNote,
  AdminResumeNoteType,
} from "../../../../../types/resumes-managment/resume";

const getInitialValues = (adminNote: ResumeAdminNote | null) => {
  if (adminNote && adminNote.id) {
    return {
      resume_id: adminNote.resume_id || "",
      admin_resume_note_type_id:
        adminNote.admin_resume_note_type?.id?.toString() || "",
    };
  } else {
    return {
      resume_id: "",
      admin_resume_note_type_id: "",
    };
  }
};

const AdminNoteSchema = Yup.object().shape({
  admin_resume_note_type_id: Yup.string().required(
    "Admin note type is required"
  ),
});

function AddEditAdminNoteForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openAdminNote: boolean) => void;
  item: ResumeAdminNote | null;
}) {
  const [adminNoteTypes, setAdminNoteTypes] = useState<AdminResumeNoteType[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const fetchAdminNoteTypes = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get("/admin_note_types");
      const { data } = response.data;
      setAdminNoteTypes(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching admin note types",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAdminNoteTypes();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: getInitialValues(item),
    validationSchema: AdminNoteSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();
        formData.append("resume_id", resumeId);
        formData.append(
          "admin_resume_note_type_id",
          values.admin_resume_note_type_id.toString()
        );

        if (item?.id) {
          await USERAXIOS.post(`/admin_notes/${item.id}`, formData);
          setResult((prevResults: any[]) =>
            prevResults.map((result: any) =>
              result.id === item.id
                ? {
                    ...result,
                    ...values,
                  }
                : result
            )
          );
          openSnackbar({
            open: true,
            message: "Admin note updated successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: { color: "success" },
          } as SnackbarProps);
        } else {
          const response = await USERAXIOS.post("/admin_notes", formData);
          const newAdminNote = response.data.data;
          setResult((prevResults: any[]) => [...prevResults, newAdminNote]);
          openSnackbar({
            open: true,
            message: "Admin note created successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: { color: "success" },
          } as SnackbarProps);
        }
        handleClose();
      } catch (error: any) {
        openSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Failed to save admin note",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  const selectedAdminNoteType = adminNoteTypes.find(
    (type) => type.id.toString() === values.admin_resume_note_type_id
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Admin Note" />
        ) : (
          <FormattedMessage id="Add Admin Note" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="admin-note-type">
                    <FormattedMessage id="Admin Note Type" />
                  </InputLabel>
                  <Autocomplete
                    id="admin-note-type"
                    options={adminNoteTypes}
                    getOptionLabel={(option) => option.title}
                    value={selectedAdminNoteType || null}
                    onChange={(event, newValue) => {
                      setFieldValue(
                        "admin_resume_note_type_id",
                        newValue ? newValue.id.toString() : ""
                      );
                    }}
                    loading={loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select admin note type"
                        error={
                          touched.admin_resume_note_type_id &&
                          Boolean(errors.admin_resume_note_type_id)
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Stack>
                          <Typography variant="subtitle1">
                            {option.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.message?.length > 50
                              ? `${option.message.substring(0, 50)}...`
                              : option.message}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  />
                  {touched.admin_resume_note_type_id &&
                    errors.admin_resume_note_type_id && (
                      <FormHelperText error>
                        {errors.admin_resume_note_type_id}
                      </FormHelperText>
                    )}
                </Stack>
              </Grid>

              {selectedAdminNoteType && (
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel>
                      <FormattedMessage id="Selected Note Type Details" />
                    </InputLabel>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          <strong>Title:</strong> {selectedAdminNoteType.title}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Message:</strong>{" "}
                          {selectedAdminNoteType.message}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong>{" "}
                          {selectedAdminNoteType.status ? "Active" : "Inactive"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditAdminNoteForm;
